////////////////////////////////////////////////////////////////////////////////
//
//
// (C) Copyright 2023 Autodesk, Inc. All rights reserved.
//
//                      ****  CONFIDENTIAL MATERIAL  ****
//
// The information contained herein is confidential, proprietary to
// Autodesk, Inc., and considered a trade secret.  Use of this information
// by anyone other than authorized employees of Autodesk, Inc. is granted
// only under a written nondisclosure agreement, expressly prescribing the
// the scope and manner of such use.
//
////////////////////////////////////////////////////////////////////////////////

//----------------------
// <auto-generated>
//     Generated using the NSwag toolchain v14.4.0.0 (NJsonSchema v11.3.2.0 (Newtonsoft.Json v13.0.0.0)) (http://NSwag.org)
// </auto-generated>
//----------------------

/* tslint:disable */
/* eslint-disable */
// ReSharper disable InconsistentNaming

import axios, {AxiosError} from 'axios';
import type {AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelToken} from 'axios';
import {
  BatchDeleteResponse,
  BulkJob,
  BulkJobPaginatedResult,
  CreateBulkJobRequest,
  CreateDirectoryRequest,
  CustomerInfoPaginatedResult,
  ExportReportRequest,
  DeckardTransferLogDto,
  EditBulkJobRequest,
  ExportResultDto,
  JobFilePaginatedResult,
  JobPaginatedResult,
  ListFilesV2Result,
  ListItemReportHistoryResult,
  ListProjectWiseFilesResult,
  ListRecentItemsResult,
  ListReportHistoryResult,
  ProjectPaginatedResult,
  ProjectWiseRepositoryPaginatedResult,
  UsageSummaryRequest,
  DetailedUsageRequest,
  ProjectWiseFileClass,
  GetRvtVersionResponse
} from "./V2Classes";
import {
  Directory360,
  File360,
  JobDirectory,
  ProblemDetails,
  ProjectWiseDirectory,
  ProjectWiseFile,
  StartUsageDownloadResult
} from "./Classes";

export interface IV2Client {
  /**
   * Creates a bulk job according to the information in the DTO.
   * @param request (optional) The object defining the parameters of the bulk job.
   * @return An object with information about the job.
   */
  createBulkJob(request?: CreateBulkJobRequest | null | undefined): Promise<BulkJob>;

  /**
   * Gets all bulk jobs for the current user.
   * @param paginationToken (optional)
   * @param limit (optional)
   * @return An object with information about the job.
   */
  listBulkJobs(paginationToken?: string | null | undefined, limit?: number | undefined): Promise<BulkJobPaginatedResult>;

  /**
   * Deletes the bulk job with the given ID.
   * @param id The bulk job ID.
   * @return The result.
   */
  deleteBulkJob(id: string): Promise<void>;

  /**
   * Edits a bulk job according to the information in the dto.
   * @param id The bulk job ID.
   * @param dto (optional) The DTO with the updated information.
   * @return An object with information about the bulk job.
   */
  editBulkJob(id: string, dto?: EditBulkJobRequest | null | undefined): Promise<BulkJob>;

  /**
   * Gets the bulk job with the given ID.
   * @param id The bulk job ID.
   * @return An object with information about the bulk job.
   */
  getBulkJob(id: string): Promise<BulkJob>;

  /**
   * Exports a report according to the given criteria.
   * @param exportReportRequest (optional) The criteria indicating which report should be exported and how.
   * @return A result indicating the status of the export.
   */
  exportReport(exportReportRequest?: ExportReportRequest | null | undefined): Promise<ExportResultDto>;

  /**
   * Gets a download URI for a report.
   * @param exportReportRequest (optional) The export request.
   * @return A DTO containing the report download information.
   */
  startReportDownload(exportReportRequest?: ExportReportRequest | null | undefined): Promise<ExportResultDto>;

  /**
   * Deletes a report according to the given report ID.
   * @param reportId The id of the report to delete.
   * @return A result indicating the status of the operation.
   */
  deleteReport(reportId: string): Promise<void>;

  /**
   * Deletes reports for the given report IDs.
   * @param reportIds (optional) The ids of the report to delete.
   * @return A result indicating the status of the operation.
   */
  deleteReports(reportIds?: string[] | null | undefined): Promise<BatchDeleteResponse>;

  /**
   * Does nothing. Method to force the ReportDto type to be included in Swagger so it can be easily serialized on the
   front end.
   * @return An empty ReportDto object.
   */
  getEmptyReportDto(): Promise<DeckardTransferLogDto>;

  getExportDownloadStatus(id: string): Promise<ExportResultDto>;

  /**
   * Gets all jobs for the current user.
   * @param paginationToken (optional)
   * @param limit (optional)
   * @param loadFilesAndFolders (optional) Whether to load the files and folders. Defaults to true.
   * @param loadItems (optional) Whether to load the job run items. Defaults to true.
   * @param newestFirst (optional) Load newest items first, defaults to true.
   * @return An object with information about the job.
   */
  listJobs(paginationToken?: string | null | undefined, limit?: number | undefined, loadFilesAndFolders?: boolean | undefined, loadItems?: boolean | undefined, newestFirst?: boolean | undefined): Promise<JobPaginatedResult>;

  /**
   * Gets the report history according to the criteria of the query.
   * @param model (optional) The name of the model for which the history should be returned, optionally. All models are returned
   if none specified.
   * @param paginationToken (optional) The pagination token.
   * @param limit (optional) Maximum number of items to return.
   * @param fullReport (optional) Whether to include all properties of the report, or only what is necessary for the UI.
   * @return A list of reports matching the query criteria.
   */
  listItemReportHistory(model?: string | null | undefined, paginationToken?: string | null | undefined, limit?: number | null | undefined, fullReport?: boolean | undefined): Promise<ListItemReportHistoryResult>;

  /**
   * Gets the report history according to the criteria of the query.
   * @param model (optional) The name of the model for which the history should be returned, optionally. All models are returned
   if none specified.
   * @param paginationToken (optional) The pagination token.
   * @param limit (optional) Maximum number of items to return.
   * @param fullReport (optional) Whether to include all properties of the report, or only what is necessary for the UI.
   * @return A list of reports matching the query criteria.
   */
  listReportHistory(model?: string | null | undefined, paginationToken?: string | null | undefined, limit?: number | null | undefined, fullReport?: boolean | undefined): Promise<ListReportHistoryResult>;

  startSummaryDownload(request?: UsageSummaryRequest | null | undefined): Promise<StartUsageDownloadResult>;

  startDetailedDownload(request?: DetailedUsageRequest | null | undefined): Promise<StartUsageDownloadResult>;

  /**
   * Lists all customers.
   * @param paginationToken (optional)
   * @param limit (optional)
   * @return All customers.
   */
  listCustomers(paginationToken?: string | null | undefined, limit?: number | undefined): Promise<CustomerInfoPaginatedResult>;

  /**
   * Gets a file by ID.
   * @param projectId The project id.
   * @param ids (optional) The file ids.
   * @return A list of recently used files.
   */
  getFiles(projectId: string, ids?: string[] | null | undefined): Promise<File360[]>;

  getProjectRvtVersion(projectId: string, directoryId: string, includeHidden?: boolean | null | undefined): Promise<GetRvtVersionResponse>;

  /**
   * Lists the recently used files.
   * @param paginationToken (optional)
   * @param limit (optional)
   * @param forgeFiles (optional) Whether to include Forge files. Defaults to true.
   * @param forgeDirectories (optional) Whether to include Forge directories. Defaults to true.
   * @param projectWiseFiles (optional) Whether to include ProjectWise files. Defaults to false.
   * @param projectWiseDirectories (optional) Whether to include ProjectWise directories. Defaults to false.
   * @return A list of recently used files.
   */
  listAllRecentFiles(paginationToken?: string | null | undefined, limit?: number | undefined, forgeFiles?: boolean | undefined, forgeDirectories?: boolean | undefined, projectWiseFiles?: boolean | undefined, projectWiseDirectories?: boolean | undefined): Promise<ListRecentItemsResult>;

  /**
   * Lists the recently used files.
   * @param paginationToken (optional)
   * @param limit (optional)
   * @return A list of recently used files.
   */
  listRecentForgeFiles(paginationToken?: string | null | undefined, limit?: number | undefined): Promise<JobFilePaginatedResult>;

  /**
   * Lists the top-level directories for the given project.
   * @param hubId The hub ID.
   * @param projectId The project ID.
   * @param includeNonProjectFiles (optional) Whether to include all root folders or only Project Files.
   * @return A list of the top-level directories for the project.
   */
  listTopLevelDirectoriesAll(hubId: string, projectId: string, includeNonProjectFiles?: boolean | undefined): Promise<JobDirectory[]>;

  /**
   * Gets a directory by ID.
   * @param projectId The project id.
   * @param parentId The parent directory id.
   * @param request (optional) The create request.
   * @return The desired directory.
   */
  createDirectory(projectId: string, parentId: string | null, request?: CreateDirectoryRequest | null | undefined): Promise<Directory360>;

  /**
   * Gets a directory by ID.
   * @param projectId The project id.
   * @param id The directory id.
   * @param allowDeleted (optional) Whether to return the directory if it has been deleted.
   * @return The desired directory.
   */
  getDirectory(projectId: string, id: string, allowDeleted?: boolean | undefined): Promise<Directory360>;

  /**
   * Gets a file by ID.
   * @param projectId The project id.
   * @param id The file id.
   * @param allowDeleted (optional) Whether to return the file if it has been deleted.
   * @param projectWiseFileClass (optional) The ProjectWise file class, optionally.
   * @return The desired file.
   */
  getFile(projectId: string, id: string, allowDeleted?: boolean | undefined, projectWiseFileClass?: ProjectWiseFileClass | null | undefined): Promise<File360>;

  /**
   * Lists files and directories for the given project and query parameters.
   * @param projectId The project ID.
   * @param directoryId The directory ID from which to list files. To list files for the whole project, set this to
   the root directory ID, and leave recursive set to its default value of true.
   * @param useClientSideFiltering (optional)
   * @param fileExtensions (optional) Gets or sets the list of file types to include. If the list is empty, include all file types.
   * @param nameFilter (optional)
   * @param paginationData_PaginationToken (optional)
   * @param paginationData_Limit (optional)
   * @param includeDeleted (optional)
   * @param listFiles (optional)
   * @param listDirectories (optional)
   * @param readToEnd (optional)
   * @param listPreviousVersions (optional)
   * @return A list of files matching the query.
   */
  listFiles(projectId: string, directoryId: string, useClientSideFiltering?: boolean | undefined, fileExtensions?: string[] | undefined, nameFilter?: string | null | undefined, paginationData_PaginationToken?: string | null | undefined, paginationData_Limit?: number | undefined, includeDeleted?: boolean | null | undefined, listFiles?: boolean | undefined, listDirectories?: boolean | undefined, readToEnd?: boolean | undefined, listPreviousVersions?: boolean | undefined): Promise<ListFilesV2Result>;

  listHubProjects(hubId: string, paginationToken?: string | null | undefined, limit?: number | undefined): Promise<ProjectPaginatedResult>;

  listImages(hubId: string, paginationToken?: string | null | undefined, limit?: number | undefined): Promise<ProjectPaginatedResult>;

  /**
   * Lists the top-level directories for the given project.
   * @param serverAddress (optional) The server address.
   * @param credentialId (optional) The credential ID, optionally.
   * @param paginationToken (optional)
   * @param limit (optional)
   * @param protocol (optional) The protocol, optionally. Defaults to HTTPS.
   * @return A list of the top-level directories for the project.
   */
  listProjectWiseRepositories(serverAddress?: string | undefined, credentialId?: string | null | undefined, paginationToken?: string | null | undefined, limit?: number | undefined, protocol?: string | null | undefined): Promise<ProjectWiseRepositoryPaginatedResult>;

  /**
   * Lists the recently used files.
   * @param paginationToken (optional)
   * @param limit (optional)
   * @param files (optional) Whether to include files. Defaults to true.
   * @param directories (optional) Whether to include directories. Defaults to true.
   * @return A list of recently used files.
   */
  listRecentFiles(paginationToken?: string | null | undefined, limit?: number | undefined, files?: boolean | undefined, directories?: boolean | undefined): Promise<ListRecentItemsResult>;

  /**
   * Lists the top-level directories for the given project.
   * @param projectId The project ID.
   * @param paginationToken (optional)
   * @param limit (optional)
   * @return A list of the top-level directories for the project.
   */
  listTopLevelDirectories(projectId: string, paginationToken?: string | null | undefined, limit?: number | undefined): Promise<ListProjectWiseFilesResult>;

  /**
   * Gets a directory by ID.
   * @param projectId The project id.
   * @param parentId The parent directory id.
   * @param request (optional) The create request.
   * @return The desired directory.
   */
  createDirectory2(projectId: string, parentId: string | null, request?: CreateDirectoryRequest | null | undefined): Promise<ProjectWiseDirectory>;

  /**
   * Gets a directory by ID.
   * @param projectId The project id.
   * @param id The directory id.
   * @param allowDeleted (optional) Whether to return the directory if it has been deleted.
   * @return The desired directory.
   */
  getDirectory2(projectId: string, id: string, allowDeleted?: boolean | undefined): Promise<ProjectWiseDirectory>;

  /**
   * Gets a file by ID.
   * @param projectId The project id.
   * @param id The file id.
   * @param allowDeleted (optional) Whether to return the file if it has been deleted.
   * @param projectWiseFileClass (optional) The ProjectWise file class, optionally.
   * @return The desired file.
   */
  getFile2(projectId: string, id: string, allowDeleted?: boolean | undefined, projectWiseFileClass?: ProjectWiseFileClass | null | undefined): Promise<ProjectWiseFile>;

  /**
   * Lists files and directories for the given project and query parameters.
   * @param projectId The project ID.
   * @param directoryId The directory ID from which to list files. To list files for the whole project, set this to
   the root directory ID, and leave recursive set to its default value of true.
   * @param useClientSideFiltering (optional)
   * @param fileExtensions (optional) Gets or sets the list of file types to include. If the list is empty, include all file types.
   * @param nameFilter (optional)
   * @param paginationData_PaginationToken (optional)
   * @param paginationData_Limit (optional)
   * @param includeDeleted (optional)
   * @param listFiles (optional)
   * @param listDirectories (optional)
   * @param readToEnd (optional)
   * @param listPreviousVersions (optional)
   * @return A list of files matching the query.
   */
  listFiles2(projectId: string, directoryId: string, useClientSideFiltering?: boolean | undefined, fileExtensions?: string[] | undefined, nameFilter?: string | null | undefined, paginationData_PaginationToken?: string | null | undefined, paginationData_Limit?: number | undefined, includeDeleted?: boolean | null | undefined, listFiles?: boolean | undefined, listDirectories?: boolean | undefined, readToEnd?: boolean | undefined, listPreviousVersions?: boolean | undefined): Promise<ListProjectWiseFilesResult>;
}

export class V2Client implements IV2Client {
  protected instance: AxiosInstance;
  protected baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

  constructor(baseUrl?: string, instance?: AxiosInstance) {

    this.instance = instance || axios.create();

    this.baseUrl = baseUrl ?? "";

  }

  /**
   * Creates a bulk job according to the information in the DTO.
   * @param request (optional) The object defining the parameters of the bulk job.
   * @return An object with information about the job.
   */
  createBulkJob(request?: CreateBulkJobRequest | null | undefined, cancelToken?: CancelToken): Promise<BulkJob> {
    let url_ = this.baseUrl + "/api/v2/job/bulk";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(request);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processCreateBulkJob(_response);
    });
  }

  protected processCreateBulkJob(response: AxiosResponse): Promise<BulkJob> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 201) {
      const _responseText = response.data;
      let result201: any = null;
      let resultData201 = _responseText;
      result201 = BulkJob.fromJS(resultData201);
      return Promise.resolve<BulkJob>(result201);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 422) {
      const _responseText = response.data;
      let result422: any = null;
      let resultData422 = _responseText;
      result422 = ProblemDetails.fromJS(resultData422);
      return throwException("A server side error occurred.", status, _responseText, _headers, result422);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<BulkJob>(null as any);
  }

  /**
   * Gets all bulk jobs for the current user.
   * @param paginationToken (optional)
   * @param limit (optional)
   * @return An object with information about the job.
   */
  listBulkJobs(paginationToken?: string | null | undefined, limit?: number | undefined, cancelToken?: CancelToken): Promise<BulkJobPaginatedResult> {
    let url_ = this.baseUrl + "/api/v2/job/bulk?";
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "PaginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit === null)
      throw new Error("The parameter 'limit' cannot be null.");
    else if (limit !== undefined)
      url_ += "Limit=" + encodeURIComponent("" + limit) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListBulkJobs(_response);
    });
  }

  protected processListBulkJobs(response: AxiosResponse): Promise<BulkJobPaginatedResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = BulkJobPaginatedResult.fromJS(resultData200);
      return Promise.resolve<BulkJobPaginatedResult>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<BulkJobPaginatedResult>(null as any);
  }

  /**
   * Deletes the bulk job with the given ID.
   * @param id The bulk job ID.
   * @return The result.
   */
  deleteBulkJob(id: string, cancelToken?: CancelToken): Promise<void> {
    let url_ = this.baseUrl + "/api/v2/job/bulk/{id}";
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "DELETE",
      url: url_,
      headers: {},
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processDeleteBulkJob(_response);
    });
  }

  protected processDeleteBulkJob(response: AxiosResponse): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 204) {
      const _responseText = response.data;
      return Promise.resolve<void>(null as any);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<void>(null as any);
  }

  /**
   * Edits a bulk job according to the information in the dto.
   * @param id The bulk job ID.
   * @param dto (optional) The DTO with the updated information.
   * @return An object with information about the bulk job.
   */
  editBulkJob(id: string, dto?: EditBulkJobRequest | null | undefined, cancelToken?: CancelToken): Promise<BulkJob> {
    let url_ = this.baseUrl + "/api/v2/job/bulk/{id}";
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(dto);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "PUT",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processEditBulkJob(_response);
    });
  }

  protected processEditBulkJob(response: AxiosResponse): Promise<BulkJob> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = BulkJob.fromJS(resultData200);
      return Promise.resolve<BulkJob>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 422) {
      const _responseText = response.data;
      let result422: any = null;
      let resultData422 = _responseText;
      result422 = ProblemDetails.fromJS(resultData422);
      return throwException("A server side error occurred.", status, _responseText, _headers, result422);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<BulkJob>(null as any);
  }

  /**
   * Gets the bulk job with the given ID.
   * @param id The bulk job ID.
   * @return An object with information about the bulk job.
   */
  getBulkJob(id: string, cancelToken?: CancelToken): Promise<BulkJob> {
    let url_ = this.baseUrl + "/api/v2/job/bulk/{id}";
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetBulkJob(_response);
    });
  }

  protected processGetBulkJob(response: AxiosResponse): Promise<BulkJob> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = BulkJob.fromJS(resultData200);
      return Promise.resolve<BulkJob>(result200);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<BulkJob>(null as any);
  }

  /**
   * Exports a report according to the given criteria.
   * @param exportReportRequest (optional) The criteria indicating which report should be exported and how.
   * @return A result indicating the status of the export.
   */
  exportReport(exportReportRequest?: ExportReportRequest | null | undefined, cancelToken?: CancelToken): Promise<ExportResultDto> {
    let url_ = this.baseUrl + "/api/v2/report/export";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(exportReportRequest);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processExportReport(_response);
    });
  }

  protected processExportReport(response: AxiosResponse): Promise<ExportResultDto> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ExportResultDto.fromJS(resultData200);
      return Promise.resolve<ExportResultDto>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ExportResultDto>(null as any);
  }

  /**
   * Gets a download URI for a report.
   * @param exportReportRequest (optional) The export request.
   * @return A DTO containing the report download information.
   */
  startReportDownload(exportReportRequest?: ExportReportRequest | null | undefined, cancelToken?: CancelToken): Promise<ExportResultDto> {
    let url_ = this.baseUrl + "/api/v2/report/download";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(exportReportRequest);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processStartReportDownload(_response);
    });
  }

  protected processStartReportDownload(response: AxiosResponse): Promise<ExportResultDto> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ExportResultDto.fromJS(resultData200);
      return Promise.resolve<ExportResultDto>(result200);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ExportResultDto>(null as any);
  }

  /**
   * Deletes a report according to the given report ID.
   * @param reportId The id of the report to delete.
   * @return A result indicating the status of the operation.
   */
  deleteReport(reportId: string, cancelToken?: CancelToken): Promise<void> {
    let url_ = this.baseUrl + "/api/v2/report/{reportId}";
    if (reportId === undefined || reportId === null)
      throw new Error("The parameter 'reportId' must be defined.");
    url_ = url_.replace("{reportId}", encodeURIComponent("" + reportId));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "DELETE",
      url: url_,
      headers: {},
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processDeleteReport(_response);
    });
  }

  protected processDeleteReport(response: AxiosResponse): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 204) {
      const _responseText = response.data;
      return Promise.resolve<void>(null as any);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<void>(null as any);
  }

  /**
   * Deletes reports for the given report IDs.
   * @param reportIds (optional) The ids of the report to delete.
   * @return A result indicating the status of the operation.
   */
  deleteReports(reportIds?: string[] | null | undefined, cancelToken?: CancelToken): Promise<BatchDeleteResponse> {
    let url_ = this.baseUrl + "/api/v2/report/batchdelete";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(reportIds);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processDeleteReports(_response);
    });
  }

  protected processDeleteReports(response: AxiosResponse): Promise<BatchDeleteResponse> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = BatchDeleteResponse.fromJS(resultData200);
      return Promise.resolve<BatchDeleteResponse>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<BatchDeleteResponse>(null as any);
  }

  /**
   * Does nothing. Method to force the ReportDto type to be included in Swagger so it can be easily serialized on the
   front end.
   * @return An empty ReportDto object.
   */
  getEmptyReportDto(cancelToken?: CancelToken): Promise<DeckardTransferLogDto> {
    let url_ = this.baseUrl + "/api/v2/report/emptydto";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetEmptyReportDto(_response);
    });
  }

  protected processGetEmptyReportDto(response: AxiosResponse): Promise<DeckardTransferLogDto> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = DeckardTransferLogDto.fromJS(resultData200);
      return Promise.resolve<DeckardTransferLogDto>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<DeckardTransferLogDto>(null as any);
  }

  getExportDownloadStatus(id: string, cancelToken?: CancelToken): Promise<ExportResultDto> {
    let url_ = this.baseUrl + "/api/v2/report/export/status/{id}";
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetExportDownloadStatus(_response);
    });
  }

  protected processGetExportDownloadStatus(response: AxiosResponse): Promise<ExportResultDto> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ExportResultDto.fromJS(resultData200);
      return Promise.resolve<ExportResultDto>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ExportResultDto>(null as any);
  }

  /**
   * Gets all jobs for the current user.
   * @param paginationToken (optional)
   * @param limit (optional)
   * @param loadFilesAndFolders (optional) Whether to load the files and folders. Defaults to true.
   * @param loadItems (optional) Whether to load the job run items. Defaults to true.
   * @param newestFirst (optional) Load newest items first, defaults to true.
   * @return An object with information about the job.
   */
  listJobs(paginationToken?: string | null | undefined, limit?: number | undefined, loadFilesAndFolders?: boolean | undefined, loadItems?: boolean | undefined, newestFirst?: boolean | undefined, cancelToken?: CancelToken): Promise<JobPaginatedResult> {
    let url_ = this.baseUrl + "/api/v2/job?";
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "PaginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit === null)
      throw new Error("The parameter 'limit' cannot be null.");
    else if (limit !== undefined)
      url_ += "Limit=" + encodeURIComponent("" + limit) + "&";
    if (loadFilesAndFolders === null)
      throw new Error("The parameter 'loadFilesAndFolders' cannot be null.");
    else if (loadFilesAndFolders !== undefined)
      url_ += "loadFilesAndFolders=" + encodeURIComponent("" + loadFilesAndFolders) + "&";
    if (loadItems === null)
      throw new Error("The parameter 'loadItems' cannot be null.");
    else if (loadItems !== undefined)
      url_ += "loadItems=" + encodeURIComponent("" + loadItems) + "&";
    if (newestFirst === null)
      throw new Error("The parameter 'newestFirst' cannot be null.");
    else if (newestFirst !== undefined)
      url_ += "newestFirst=" + encodeURIComponent("" + newestFirst) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListJobs(_response);
    });
  }

  protected processListJobs(response: AxiosResponse): Promise<JobPaginatedResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = JobPaginatedResult.fromJS(resultData200);
      return Promise.resolve<JobPaginatedResult>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<JobPaginatedResult>(null as any);
  }

  /**
   * Gets the report history according to the criteria of the query.
   * @param model (optional) The name of the model for which the history should be returned, optionally. All models are returned
   if none specified.
   * @param paginationToken (optional) The pagination token.
   * @param limit (optional) Maximum number of items to return.
   * @param fullReport (optional) Whether to include all properties of the report, or only what is necessary for the UI.
   * @return A list of reports matching the query criteria.
   */
  listItemReportHistory(model?: string | null | undefined, paginationToken?: string | null | undefined, limit?: number | null | undefined, fullReport?: boolean | undefined, cancelToken?: CancelToken): Promise<ListItemReportHistoryResult> {
    let url_ = this.baseUrl + "/api/v2/report/history?";
    if (model !== undefined && model !== null)
      url_ += "model=" + encodeURIComponent("" + model) + "&";
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "paginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit !== undefined && limit !== null)
      url_ += "limit=" + encodeURIComponent("" + limit) + "&";
    if (fullReport === null)
      throw new Error("The parameter 'fullReport' cannot be null.");
    else if (fullReport !== undefined)
      url_ += "fullReport=" + encodeURIComponent("" + fullReport) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListItemReportHistory(_response);
    });
  }

  protected processListItemReportHistory(response: AxiosResponse): Promise<ListItemReportHistoryResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ListItemReportHistoryResult.fromJS(resultData200);
      return Promise.resolve<ListItemReportHistoryResult>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ListItemReportHistoryResult>(null as any);
  }

  /**
   * Gets the report history according to the criteria of the query.
   * @param model (optional) The name of the model for which the history should be returned, optionally. All models are returned
   if none specified.
   * @param paginationToken (optional) The pagination token.
   * @param limit (optional) Maximum number of items to return.
   * @param fullReport (optional) Whether to include all properties of the report, or only what is necessary for the UI.
   * @return A list of reports matching the query criteria.
   */
  listReportHistory(model?: string | null | undefined, paginationToken?: string | null | undefined, limit?: number | null | undefined, fullReport?: boolean | undefined, cancelToken?: CancelToken): Promise<ListReportHistoryResult> {
    let url_ = this.baseUrl + "/api/v2/report?";
    if (model !== undefined && model !== null)
      url_ += "model=" + encodeURIComponent("" + model) + "&";
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "paginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit !== undefined && limit !== null)
      url_ += "limit=" + encodeURIComponent("" + limit) + "&";
    if (fullReport === null)
      throw new Error("The parameter 'fullReport' cannot be null.");
    else if (fullReport !== undefined)
      url_ += "fullReport=" + encodeURIComponent("" + fullReport) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListReportHistory(_response);
    });
  }

  protected processListReportHistory(response: AxiosResponse): Promise<ListReportHistoryResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ListReportHistoryResult.fromJS(resultData200);
      return Promise.resolve<ListReportHistoryResult>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ListReportHistoryResult>(null as any);
  }

  startSummaryDownload(request?: UsageSummaryRequest | null | undefined, cancelToken?: CancelToken): Promise<StartUsageDownloadResult> {
    let url_ = this.baseUrl + "/api/v2/usage/download/summary";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(request);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processStartSummaryDownload(_response);
    });
  }

  protected processStartSummaryDownload(response: AxiosResponse): Promise<StartUsageDownloadResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = StartUsageDownloadResult.fromJS(resultData200);
      return Promise.resolve<StartUsageDownloadResult>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<StartUsageDownloadResult>(null as any);
  }

  startDetailedDownload(request?: DetailedUsageRequest | null | undefined, cancelToken?: CancelToken): Promise<StartUsageDownloadResult> {
    let url_ = this.baseUrl + "/api/v2/usage/download/detailed";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(request);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processStartDetailedDownload(_response);
    });
  }

  protected processStartDetailedDownload(response: AxiosResponse): Promise<StartUsageDownloadResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = StartUsageDownloadResult.fromJS(resultData200);
      return Promise.resolve<StartUsageDownloadResult>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<StartUsageDownloadResult>(null as any);
  }

  /**
   * Lists all customers.
   * @param paginationToken (optional)
   * @param limit (optional)
   * @return All customers.
   */
  listCustomers(paginationToken?: string | null | undefined, limit?: number | undefined, cancelToken?: CancelToken): Promise<CustomerInfoPaginatedResult> {
    let url_ = this.baseUrl + "/api/v2/customer?";
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "PaginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit === null)
      throw new Error("The parameter 'limit' cannot be null.");
    else if (limit !== undefined)
      url_ += "Limit=" + encodeURIComponent("" + limit) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListCustomers(_response);
    });
  }

  protected processListCustomers(response: AxiosResponse): Promise<CustomerInfoPaginatedResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = CustomerInfoPaginatedResult.fromJS(resultData200);
      return Promise.resolve<CustomerInfoPaginatedResult>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<CustomerInfoPaginatedResult>(null as any);
  }

  /**
   * Gets a file by ID.
   * @param projectId The project id.
   * @param ids (optional) The file ids.
   * @return A list of recently used files.
   */
  getFiles(projectId: string, ids?: string[] | null | undefined, cancelToken?: CancelToken): Promise<File360[]> {
    let url_ = this.baseUrl + "/api/v2/file/batch/{projectId}";
    if (projectId === undefined || projectId === null)
      throw new Error("The parameter 'projectId' must be defined.");
    url_ = url_.replace("{projectId}", encodeURIComponent("" + projectId));
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(ids);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "GET",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetFiles(_response);
    });
  }

  protected processGetFiles(response: AxiosResponse): Promise<File360[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      if (Array.isArray(resultData200)) {
        result200 = [] as any;
        for (let item of resultData200)
          result200!.push(File360.fromJS(item));
      } else {
        result200 = <any>null;
      }
      return Promise.resolve<File360[]>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<File360[]>(null as any);
  }

  getProjectRvtVersion(projectId: string, directoryId: string, includeHidden?: boolean | null | undefined, cancelToken?: CancelToken): Promise<GetRvtVersionResponse> {
    let url_ = this.baseUrl + "/api/v2/file/{projectId}/{directoryId}/version?";
    if (projectId === undefined || projectId === null)
      throw new Error("The parameter 'projectId' must be defined.");
    url_ = url_.replace("{projectId}", encodeURIComponent("" + projectId));
    if (directoryId === undefined || directoryId === null)
      throw new Error("The parameter 'directoryId' must be defined.");
    url_ = url_.replace("{directoryId}", encodeURIComponent("" + directoryId));
    if (includeHidden !== undefined && includeHidden !== null)
      url_ += "includeHidden=" + encodeURIComponent("" + includeHidden) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetProjectRvtVersion(_response);
    });
  }

  protected processGetProjectRvtVersion(response: AxiosResponse): Promise<GetRvtVersionResponse> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = GetRvtVersionResponse.fromJS(resultData200);
      return Promise.resolve<GetRvtVersionResponse>(result200);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<GetRvtVersionResponse>(null as any);
  }

  /**
   * Lists the recently used files.
   * @param paginationToken (optional)
   * @param limit (optional)
   * @param forgeFiles (optional) Whether to include Forge files. Defaults to true.
   * @param forgeDirectories (optional) Whether to include Forge directories. Defaults to true.
   * @param projectWiseFiles (optional) Whether to include ProjectWise files. Defaults to false.
   * @param projectWiseDirectories (optional) Whether to include ProjectWise directories. Defaults to false.
   * @return A list of recently used files.
   */
  listAllRecentFiles(paginationToken?: string | null | undefined, limit?: number | undefined, forgeFiles?: boolean | undefined, forgeDirectories?: boolean | undefined, projectWiseFiles?: boolean | undefined, projectWiseDirectories?: boolean | undefined, cancelToken?: CancelToken): Promise<ListRecentItemsResult> {
    let url_ = this.baseUrl + "/api/v2/file/recent/all?";
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "PaginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit === null)
      throw new Error("The parameter 'limit' cannot be null.");
    else if (limit !== undefined)
      url_ += "Limit=" + encodeURIComponent("" + limit) + "&";
    if (forgeFiles === null)
      throw new Error("The parameter 'forgeFiles' cannot be null.");
    else if (forgeFiles !== undefined)
      url_ += "forgeFiles=" + encodeURIComponent("" + forgeFiles) + "&";
    if (forgeDirectories === null)
      throw new Error("The parameter 'forgeDirectories' cannot be null.");
    else if (forgeDirectories !== undefined)
      url_ += "forgeDirectories=" + encodeURIComponent("" + forgeDirectories) + "&";
    if (projectWiseFiles === null)
      throw new Error("The parameter 'projectWiseFiles' cannot be null.");
    else if (projectWiseFiles !== undefined)
      url_ += "projectWiseFiles=" + encodeURIComponent("" + projectWiseFiles) + "&";
    if (projectWiseDirectories === null)
      throw new Error("The parameter 'projectWiseDirectories' cannot be null.");
    else if (projectWiseDirectories !== undefined)
      url_ += "projectWiseDirectories=" + encodeURIComponent("" + projectWiseDirectories) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListAllRecentFiles(_response);
    });
  }

  protected processListAllRecentFiles(response: AxiosResponse): Promise<ListRecentItemsResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ListRecentItemsResult.fromJS(resultData200);
      return Promise.resolve<ListRecentItemsResult>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ListRecentItemsResult>(null as any);
  }

  /**
   * Lists the recently used files.
   * @param paginationToken (optional)
   * @param limit (optional)
   * @return A list of recently used files.
   */
  listRecentForgeFiles(paginationToken?: string | null | undefined, limit?: number | undefined, cancelToken?: CancelToken): Promise<JobFilePaginatedResult> {
    let url_ = this.baseUrl + "/api/v2/file/recent?";
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "PaginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit === null)
      throw new Error("The parameter 'limit' cannot be null.");
    else if (limit !== undefined)
      url_ += "Limit=" + encodeURIComponent("" + limit) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListRecentForgeFiles(_response);
    });
  }

  protected processListRecentForgeFiles(response: AxiosResponse): Promise<JobFilePaginatedResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = JobFilePaginatedResult.fromJS(resultData200);
      return Promise.resolve<JobFilePaginatedResult>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<JobFilePaginatedResult>(null as any);
  }

  /**
   * Lists the top-level directories for the given project.
   * @param hubId The hub ID.
   * @param projectId The project ID.
   * @param includeNonProjectFiles (optional) Whether to include all root folders or only Project Files.
   * @return A list of the top-level directories for the project.
   */
  listTopLevelDirectoriesAll(hubId: string, projectId: string, includeNonProjectFiles?: boolean | undefined, cancelToken?: CancelToken): Promise<JobDirectory[]> {
    let url_ = this.baseUrl + "/api/v2/file/top/{hubId}/{projectId}?";
    if (hubId === undefined || hubId === null)
      throw new Error("The parameter 'hubId' must be defined.");
    url_ = url_.replace("{hubId}", encodeURIComponent("" + hubId));
    if (projectId === undefined || projectId === null)
      throw new Error("The parameter 'projectId' must be defined.");
    url_ = url_.replace("{projectId}", encodeURIComponent("" + projectId));
    if (includeNonProjectFiles === null)
      throw new Error("The parameter 'includeNonProjectFiles' cannot be null.");
    else if (includeNonProjectFiles !== undefined)
      url_ += "includeNonProjectFiles=" + encodeURIComponent("" + includeNonProjectFiles) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListTopLevelDirectoriesAll(_response);
    });
  }

  protected processListTopLevelDirectoriesAll(response: AxiosResponse): Promise<JobDirectory[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      if (Array.isArray(resultData200)) {
        result200 = [] as any;
        for (let item of resultData200)
          result200!.push(JobDirectory.fromJS(item));
      } else {
        result200 = <any>null;
      }
      return Promise.resolve<JobDirectory[]>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<JobDirectory[]>(null as any);
  }

  /**
   * Gets a directory by ID.
   * @param projectId The project id.
   * @param parentId The parent directory id.
   * @param request (optional) The create request.
   * @return The desired directory.
   */
  createDirectory(projectId: string, parentId: string | null, request?: CreateDirectoryRequest | null | undefined, cancelToken?: CancelToken): Promise<Directory360> {
    let url_ = this.baseUrl + "/api/v2/file/dir/{projectId}/{parentId}";
    if (projectId === undefined || projectId === null)
      throw new Error("The parameter 'projectId' must be defined.");
    url_ = url_.replace("{projectId}", encodeURIComponent("" + projectId));
    if (parentId === undefined || parentId === null)
      throw new Error("The parameter 'parentId' must be defined.");
    url_ = url_.replace("{parentId}", encodeURIComponent("" + parentId));
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(request);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processCreateDirectory(_response);
    });
  }

  protected processCreateDirectory(response: AxiosResponse): Promise<Directory360> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 201) {
      const _responseText = response.data;
      let result201: any = null;
      let resultData201 = _responseText;
      result201 = Directory360.fromJS(resultData201);
      return Promise.resolve<Directory360>(result201);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 409) {
      const _responseText = response.data;
      let result409: any = null;
      let resultData409 = _responseText;
      result409 = ProblemDetails.fromJS(resultData409);
      return throwException("A server side error occurred.", status, _responseText, _headers, result409);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<Directory360>(null as any);
  }

  /**
   * Gets a directory by ID.
   * @param projectId The project id.
   * @param id The directory id.
   * @param allowDeleted (optional) Whether to return the directory if it has been deleted.
   * @return The desired directory.
   */
  getDirectory(projectId: string, id: string, allowDeleted?: boolean | undefined, cancelToken?: CancelToken): Promise<Directory360> {
    let url_ = this.baseUrl + "/api/v2/file/dir/{projectId}/{id}?";
    if (projectId === undefined || projectId === null)
      throw new Error("The parameter 'projectId' must be defined.");
    url_ = url_.replace("{projectId}", encodeURIComponent("" + projectId));
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    if (allowDeleted === null)
      throw new Error("The parameter 'allowDeleted' cannot be null.");
    else if (allowDeleted !== undefined)
      url_ += "allowDeleted=" + encodeURIComponent("" + allowDeleted) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetDirectory(_response);
    });
  }

  protected processGetDirectory(response: AxiosResponse): Promise<Directory360> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = Directory360.fromJS(resultData200);
      return Promise.resolve<Directory360>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status === 410) {
      const _responseText = response.data;
      let result410: any = null;
      let resultData410 = _responseText;
      result410 = ProblemDetails.fromJS(resultData410);
      return throwException("A server side error occurred.", status, _responseText, _headers, result410);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<Directory360>(null as any);
  }

  /**
   * Gets a file by ID.
   * @param projectId The project id.
   * @param id The file id.
   * @param allowDeleted (optional) Whether to return the file if it has been deleted.
   * @param projectWiseFileClass (optional) The ProjectWise file class, optionally.
   * @return The desired file.
   */
  getFile(projectId: string, id: string, allowDeleted?: boolean | undefined, projectWiseFileClass?: ProjectWiseFileClass | null | undefined, cancelToken?: CancelToken): Promise<File360> {
    let url_ = this.baseUrl + "/api/v2/file/{projectId}/{id}?";
    if (projectId === undefined || projectId === null)
      throw new Error("The parameter 'projectId' must be defined.");
    url_ = url_.replace("{projectId}", encodeURIComponent("" + projectId));
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    if (allowDeleted === null)
      throw new Error("The parameter 'allowDeleted' cannot be null.");
    else if (allowDeleted !== undefined)
      url_ += "allowDeleted=" + encodeURIComponent("" + allowDeleted) + "&";
    if (projectWiseFileClass !== undefined && projectWiseFileClass !== null)
      url_ += "projectWiseFileClass=" + encodeURIComponent("" + projectWiseFileClass) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetFile(_response);
    });
  }

  protected processGetFile(response: AxiosResponse): Promise<File360> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = File360.fromJS(resultData200);
      return Promise.resolve<File360>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status === 410) {
      const _responseText = response.data;
      let result410: any = null;
      let resultData410 = _responseText;
      result410 = ProblemDetails.fromJS(resultData410);
      return throwException("A server side error occurred.", status, _responseText, _headers, result410);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<File360>(null as any);
  }

  /**
   * Lists files and directories for the given project and query parameters.
   * @param projectId The project ID.
   * @param directoryId The directory ID from which to list files. To list files for the whole project, set this to
   the root directory ID, and leave recursive set to its default value of true.
   * @param useClientSideFiltering (optional)
   * @param fileExtensions (optional) Gets or sets the list of file types to include. If the list is empty, include all file types.
   * @param nameFilter (optional)
   * @param paginationData_PaginationToken (optional)
   * @param paginationData_Limit (optional)
   * @param includeDeleted (optional)
   * @param listFiles (optional)
   * @param listDirectories (optional)
   * @param readToEnd (optional)
   * @param listPreviousVersions (optional)
   * @return A list of files matching the query.
   */
  listFiles(projectId: string, directoryId: string, useClientSideFiltering?: boolean | undefined, fileExtensions?: string[] | undefined, nameFilter?: string | null | undefined, paginationData_PaginationToken?: string | null | undefined, paginationData_Limit?: number | undefined, includeDeleted?: boolean | null | undefined, listFiles?: boolean | undefined, listDirectories?: boolean | undefined, readToEnd?: boolean | undefined, listPreviousVersions?: boolean | undefined, cancelToken?: CancelToken): Promise<ListFilesV2Result> {
    let url_ = this.baseUrl + "/api/v2/file/list/{projectId}/{directoryId}?";
    if (projectId === undefined || projectId === null)
      throw new Error("The parameter 'projectId' must be defined.");
    url_ = url_.replace("{projectId}", encodeURIComponent("" + projectId));
    if (directoryId === undefined || directoryId === null)
      throw new Error("The parameter 'directoryId' must be defined.");
    url_ = url_.replace("{directoryId}", encodeURIComponent("" + directoryId));
    if (useClientSideFiltering === null)
      throw new Error("The parameter 'useClientSideFiltering' cannot be null.");
    else if (useClientSideFiltering !== undefined)
      url_ += "UseClientSideFiltering=" + encodeURIComponent("" + useClientSideFiltering) + "&";
    if (fileExtensions === null)
      throw new Error("The parameter 'fileExtensions' cannot be null.");
    else if (fileExtensions !== undefined)
      fileExtensions && fileExtensions.forEach(item => {
        url_ += "FileExtensions=" + encodeURIComponent("" + item) + "&";
      });
    if (nameFilter !== undefined && nameFilter !== null)
      url_ += "NameFilter=" + encodeURIComponent("" + nameFilter) + "&";
    if (paginationData_PaginationToken !== undefined && paginationData_PaginationToken !== null)
      url_ += "PaginationData.PaginationToken=" + encodeURIComponent("" + paginationData_PaginationToken) + "&";
    if (paginationData_Limit === null)
      throw new Error("The parameter 'paginationData_Limit' cannot be null.");
    else if (paginationData_Limit !== undefined)
      url_ += "PaginationData.Limit=" + encodeURIComponent("" + paginationData_Limit) + "&";
    if (includeDeleted !== undefined && includeDeleted !== null)
      url_ += "IncludeDeleted=" + encodeURIComponent("" + includeDeleted) + "&";
    if (listFiles === null)
      throw new Error("The parameter 'listFiles' cannot be null.");
    else if (listFiles !== undefined)
      url_ += "ListFiles=" + encodeURIComponent("" + listFiles) + "&";
    if (listDirectories === null)
      throw new Error("The parameter 'listDirectories' cannot be null.");
    else if (listDirectories !== undefined)
      url_ += "ListDirectories=" + encodeURIComponent("" + listDirectories) + "&";
    if (readToEnd === null)
      throw new Error("The parameter 'readToEnd' cannot be null.");
    else if (readToEnd !== undefined)
      url_ += "ReadToEnd=" + encodeURIComponent("" + readToEnd) + "&";
    if (listPreviousVersions === null)
      throw new Error("The parameter 'listPreviousVersions' cannot be null.");
    else if (listPreviousVersions !== undefined)
      url_ += "ListPreviousVersions=" + encodeURIComponent("" + listPreviousVersions) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListFiles(_response);
    });
  }

  protected processListFiles(response: AxiosResponse): Promise<ListFilesV2Result> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ListFilesV2Result.fromJS(resultData200);
      return Promise.resolve<ListFilesV2Result>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ListFilesV2Result>(null as any);
  }

  listHubProjects(hubId: string, paginationToken?: string | null | undefined, limit?: number | undefined, cancelToken?: CancelToken): Promise<ProjectPaginatedResult> {
    let url_ = this.baseUrl + "/api/v2/project/{hubId}?";
    if (hubId === undefined || hubId === null)
      throw new Error("The parameter 'hubId' must be defined.");
    url_ = url_.replace("{hubId}", encodeURIComponent("" + hubId));
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "PaginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit === null)
      throw new Error("The parameter 'limit' cannot be null.");
    else if (limit !== undefined)
      url_ += "Limit=" + encodeURIComponent("" + limit) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListHubProjects(_response);
    });
  }

  protected processListHubProjects(response: AxiosResponse): Promise<ProjectPaginatedResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ProjectPaginatedResult.fromJS(resultData200);
      return Promise.resolve<ProjectPaginatedResult>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ProjectPaginatedResult>(null as any);
  }

  listImages(hubId: string, paginationToken?: string | null | undefined, limit?: number | undefined, cancelToken?: CancelToken): Promise<ProjectPaginatedResult> {
    let url_ = this.baseUrl + "/api/v2/project/{hubId}/images?";
    if (hubId === undefined || hubId === null)
      throw new Error("The parameter 'hubId' must be defined.");
    url_ = url_.replace("{hubId}", encodeURIComponent("" + hubId));
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "PaginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit === null)
      throw new Error("The parameter 'limit' cannot be null.");
    else if (limit !== undefined)
      url_ += "Limit=" + encodeURIComponent("" + limit) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListImages(_response);
    });
  }

  protected processListImages(response: AxiosResponse): Promise<ProjectPaginatedResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ProjectPaginatedResult.fromJS(resultData200);
      return Promise.resolve<ProjectPaginatedResult>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ProjectPaginatedResult>(null as any);
  }

  /**
   * Lists the top-level directories for the given project.
   * @param serverAddress (optional) The server address.
   * @param credentialId (optional) The credential ID, optionally.
   * @param paginationToken (optional)
   * @param limit (optional)
   * @param protocol (optional) The protocol, optionally. Defaults to HTTPS.
   * @return A list of the top-level directories for the project.
   */
  listProjectWiseRepositories(serverAddress?: string | undefined, credentialId?: string | null | undefined, paginationToken?: string | null | undefined, limit?: number | undefined, protocol?: string | null | undefined, cancelToken?: CancelToken): Promise<ProjectWiseRepositoryPaginatedResult> {
    let url_ = this.baseUrl + "/api/v2/pw/file/repositories?";
    if (serverAddress === null)
      throw new Error("The parameter 'serverAddress' cannot be null.");
    else if (serverAddress !== undefined)
      url_ += "serverAddress=" + encodeURIComponent("" + serverAddress) + "&";
    if (credentialId !== undefined && credentialId !== null)
      url_ += "credentialId=" + encodeURIComponent("" + credentialId) + "&";
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "PaginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit === null)
      throw new Error("The parameter 'limit' cannot be null.");
    else if (limit !== undefined)
      url_ += "Limit=" + encodeURIComponent("" + limit) + "&";
    if (protocol !== undefined && protocol !== null)
      url_ += "protocol=" + encodeURIComponent("" + protocol) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListProjectWiseRepositories(_response);
    });
  }

  protected processListProjectWiseRepositories(response: AxiosResponse): Promise<ProjectWiseRepositoryPaginatedResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ProjectWiseRepositoryPaginatedResult.fromJS(resultData200);
      return Promise.resolve<ProjectWiseRepositoryPaginatedResult>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ProjectWiseRepositoryPaginatedResult>(null as any);
  }

  /**
   * Lists the recently used files.
   * @param paginationToken (optional)
   * @param limit (optional)
   * @param files (optional) Whether to include files. Defaults to true.
   * @param directories (optional) Whether to include directories. Defaults to true.
   * @return A list of recently used files.
   */
  listRecentFiles(paginationToken?: string | null | undefined, limit?: number | undefined, files?: boolean | undefined, directories?: boolean | undefined, cancelToken?: CancelToken): Promise<ListRecentItemsResult> {
    let url_ = this.baseUrl + "/api/v2/pw/file/recent?";
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "PaginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit === null)
      throw new Error("The parameter 'limit' cannot be null.");
    else if (limit !== undefined)
      url_ += "Limit=" + encodeURIComponent("" + limit) + "&";
    if (files === null)
      throw new Error("The parameter 'files' cannot be null.");
    else if (files !== undefined)
      url_ += "files=" + encodeURIComponent("" + files) + "&";
    if (directories === null)
      throw new Error("The parameter 'directories' cannot be null.");
    else if (directories !== undefined)
      url_ += "directories=" + encodeURIComponent("" + directories) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListRecentFiles(_response);
    });
  }

  protected processListRecentFiles(response: AxiosResponse): Promise<ListRecentItemsResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ListRecentItemsResult.fromJS(resultData200);
      return Promise.resolve<ListRecentItemsResult>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ListRecentItemsResult>(null as any);
  }

  /**
   * Lists the top-level directories for the given project.
   * @param projectId The project ID.
   * @param paginationToken (optional)
   * @param limit (optional)
   * @return A list of the top-level directories for the project.
   */
  listTopLevelDirectories(projectId: string, paginationToken?: string | null | undefined, limit?: number | undefined, cancelToken?: CancelToken): Promise<ListProjectWiseFilesResult> {
    let url_ = this.baseUrl + "/api/v2/pw/file/top/{projectId}?";
    if (projectId === undefined || projectId === null)
      throw new Error("The parameter 'projectId' must be defined.");
    url_ = url_.replace("{projectId}", encodeURIComponent("" + projectId));
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "PaginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit === null)
      throw new Error("The parameter 'limit' cannot be null.");
    else if (limit !== undefined)
      url_ += "Limit=" + encodeURIComponent("" + limit) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListTopLevelDirectories(_response);
    });
  }

  protected processListTopLevelDirectories(response: AxiosResponse): Promise<ListProjectWiseFilesResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ListProjectWiseFilesResult.fromJS(resultData200);
      return Promise.resolve<ListProjectWiseFilesResult>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ListProjectWiseFilesResult>(null as any);
  }

  /**
   * Gets a directory by ID.
   * @param projectId The project id.
   * @param parentId The parent directory id.
   * @param request (optional) The create request.
   * @return The desired directory.
   */
  createDirectory2(projectId: string, parentId: string | null, request?: CreateDirectoryRequest | null | undefined, cancelToken?: CancelToken): Promise<ProjectWiseDirectory> {
    let url_ = this.baseUrl + "/api/v2/pw/file/dir/{projectId}/{parentId}";
    if (projectId === undefined || projectId === null)
      throw new Error("The parameter 'projectId' must be defined.");
    url_ = url_.replace("{projectId}", encodeURIComponent("" + projectId));
    if (parentId === undefined || parentId === null)
      throw new Error("The parameter 'parentId' must be defined.");
    url_ = url_.replace("{parentId}", encodeURIComponent("" + parentId));
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(request);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processCreateDirectory2(_response);
    });
  }

  protected processCreateDirectory2(response: AxiosResponse): Promise<ProjectWiseDirectory> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 201) {
      const _responseText = response.data;
      let result201: any = null;
      let resultData201 = _responseText;
      result201 = ProjectWiseDirectory.fromJS(resultData201);
      return Promise.resolve<ProjectWiseDirectory>(result201);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 409) {
      const _responseText = response.data;
      let result409: any = null;
      let resultData409 = _responseText;
      result409 = ProblemDetails.fromJS(resultData409);
      return throwException("A server side error occurred.", status, _responseText, _headers, result409);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ProjectWiseDirectory>(null as any);
  }

  /**
   * Gets a directory by ID.
   * @param projectId The project id.
   * @param id The directory id.
   * @param allowDeleted (optional) Whether to return the directory if it has been deleted.
   * @return The desired directory.
   */
  getDirectory2(projectId: string, id: string, allowDeleted?: boolean | undefined, cancelToken?: CancelToken): Promise<ProjectWiseDirectory> {
    let url_ = this.baseUrl + "/api/v2/pw/file/dir/{projectId}/{id}?";
    if (projectId === undefined || projectId === null)
      throw new Error("The parameter 'projectId' must be defined.");
    url_ = url_.replace("{projectId}", encodeURIComponent("" + projectId));
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    if (allowDeleted === null)
      throw new Error("The parameter 'allowDeleted' cannot be null.");
    else if (allowDeleted !== undefined)
      url_ += "allowDeleted=" + encodeURIComponent("" + allowDeleted) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetDirectory2(_response);
    });
  }

  protected processGetDirectory2(response: AxiosResponse): Promise<ProjectWiseDirectory> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ProjectWiseDirectory.fromJS(resultData200);
      return Promise.resolve<ProjectWiseDirectory>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status === 410) {
      const _responseText = response.data;
      let result410: any = null;
      let resultData410 = _responseText;
      result410 = ProblemDetails.fromJS(resultData410);
      return throwException("A server side error occurred.", status, _responseText, _headers, result410);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ProjectWiseDirectory>(null as any);
  }

  /**
   * Gets a file by ID.
   * @param projectId The project id.
   * @param id The file id.
   * @param allowDeleted (optional) Whether to return the file if it has been deleted.
   * @param projectWiseFileClass (optional) The ProjectWise file class, optionally.
   * @return The desired file.
   */
  getFile2(projectId: string, id: string, allowDeleted?: boolean | undefined, projectWiseFileClass?: ProjectWiseFileClass | null | undefined, cancelToken?: CancelToken): Promise<ProjectWiseFile> {
    let url_ = this.baseUrl + "/api/v2/pw/file/{projectId}/{id}?";
    if (projectId === undefined || projectId === null)
      throw new Error("The parameter 'projectId' must be defined.");
    url_ = url_.replace("{projectId}", encodeURIComponent("" + projectId));
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    if (allowDeleted === null)
      throw new Error("The parameter 'allowDeleted' cannot be null.");
    else if (allowDeleted !== undefined)
      url_ += "allowDeleted=" + encodeURIComponent("" + allowDeleted) + "&";
    if (projectWiseFileClass !== undefined && projectWiseFileClass !== null)
      url_ += "projectWiseFileClass=" + encodeURIComponent("" + projectWiseFileClass) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetFile2(_response);
    });
  }

  protected processGetFile2(response: AxiosResponse): Promise<ProjectWiseFile> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ProjectWiseFile.fromJS(resultData200);
      return Promise.resolve<ProjectWiseFile>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status === 410) {
      const _responseText = response.data;
      let result410: any = null;
      let resultData410 = _responseText;
      result410 = ProblemDetails.fromJS(resultData410);
      return throwException("A server side error occurred.", status, _responseText, _headers, result410);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ProjectWiseFile>(null as any);
  }

  /**
   * Lists files and directories for the given project and query parameters.
   * @param projectId The project ID.
   * @param directoryId The directory ID from which to list files. To list files for the whole project, set this to
   the root directory ID, and leave recursive set to its default value of true.
   * @param useClientSideFiltering (optional)
   * @param fileExtensions (optional) Gets or sets the list of file types to include. If the list is empty, include all file types.
   * @param nameFilter (optional)
   * @param paginationData_PaginationToken (optional)
   * @param paginationData_Limit (optional)
   * @param includeDeleted (optional)
   * @param listFiles (optional)
   * @param listDirectories (optional)
   * @param readToEnd (optional)
   * @param listPreviousVersions (optional)
   * @return A list of files matching the query.
   */
  listFiles2(projectId: string, directoryId: string, useClientSideFiltering?: boolean | undefined, fileExtensions?: string[] | undefined, nameFilter?: string | null | undefined, paginationData_PaginationToken?: string | null | undefined, paginationData_Limit?: number | undefined, includeDeleted?: boolean | null | undefined, listFiles?: boolean | undefined, listDirectories?: boolean | undefined, readToEnd?: boolean | undefined, listPreviousVersions?: boolean | undefined, cancelToken?: CancelToken): Promise<ListProjectWiseFilesResult> {
    let url_ = this.baseUrl + "/api/v2/pw/file/list/{projectId}/{directoryId}?";
    if (projectId === undefined || projectId === null)
      throw new Error("The parameter 'projectId' must be defined.");
    url_ = url_.replace("{projectId}", encodeURIComponent("" + projectId));
    if (directoryId === undefined || directoryId === null)
      throw new Error("The parameter 'directoryId' must be defined.");
    url_ = url_.replace("{directoryId}", encodeURIComponent("" + directoryId));
    if (useClientSideFiltering === null)
      throw new Error("The parameter 'useClientSideFiltering' cannot be null.");
    else if (useClientSideFiltering !== undefined)
      url_ += "UseClientSideFiltering=" + encodeURIComponent("" + useClientSideFiltering) + "&";
    if (fileExtensions === null)
      throw new Error("The parameter 'fileExtensions' cannot be null.");
    else if (fileExtensions !== undefined)
      fileExtensions && fileExtensions.forEach(item => {
        url_ += "FileExtensions=" + encodeURIComponent("" + item) + "&";
      });
    if (nameFilter !== undefined && nameFilter !== null)
      url_ += "NameFilter=" + encodeURIComponent("" + nameFilter) + "&";
    if (paginationData_PaginationToken !== undefined && paginationData_PaginationToken !== null)
      url_ += "PaginationData.PaginationToken=" + encodeURIComponent("" + paginationData_PaginationToken) + "&";
    if (paginationData_Limit === null)
      throw new Error("The parameter 'paginationData_Limit' cannot be null.");
    else if (paginationData_Limit !== undefined)
      url_ += "PaginationData.Limit=" + encodeURIComponent("" + paginationData_Limit) + "&";
    if (includeDeleted !== undefined && includeDeleted !== null)
      url_ += "IncludeDeleted=" + encodeURIComponent("" + includeDeleted) + "&";
    if (listFiles === null)
      throw new Error("The parameter 'listFiles' cannot be null.");
    else if (listFiles !== undefined)
      url_ += "ListFiles=" + encodeURIComponent("" + listFiles) + "&";
    if (listDirectories === null)
      throw new Error("The parameter 'listDirectories' cannot be null.");
    else if (listDirectories !== undefined)
      url_ += "ListDirectories=" + encodeURIComponent("" + listDirectories) + "&";
    if (readToEnd === null)
      throw new Error("The parameter 'readToEnd' cannot be null.");
    else if (readToEnd !== undefined)
      url_ += "ReadToEnd=" + encodeURIComponent("" + readToEnd) + "&";
    if (listPreviousVersions === null)
      throw new Error("The parameter 'listPreviousVersions' cannot be null.");
    else if (listPreviousVersions !== undefined)
      url_ += "ListPreviousVersions=" + encodeURIComponent("" + listPreviousVersions) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListFiles2(_response);
    });
  }

  protected processListFiles2(response: AxiosResponse): Promise<ListProjectWiseFilesResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ListProjectWiseFilesResult.fromJS(resultData200);
      return Promise.resolve<ListProjectWiseFilesResult>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ListProjectWiseFilesResult>(null as any);
  }
}

export class ApiException extends Error {
  override message: string;
  status: number;
  response: string;
  headers: { [key: string]: any; };
  result: any;

  constructor(message: string, status: number, response: string, headers: { [key: string]: any; }, result: any) {
    super();

    this.message = message;
    this.status = status;
    this.response = response;
    this.headers = headers;
    this.result = result;
  }

  protected isApiException = true;

  static isApiException(obj: any): obj is ApiException {
    return obj.isApiException === true;
  }
}

function throwException(message: string, status: number, response: string, headers: {
  [key: string]: any;
}, result?: any): any {
  if (result !== null && result !== undefined)
    throw result;
  else
    throw new ApiException(message, status, response, headers, null);
}

function isAxiosError(obj: any): obj is AxiosError {
  return obj && obj.isAxiosError === true;
}